import React, { useCallback, useEffect, useState } from "react";
import {
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    IconButton,
    Menu,
    MenuItem,
    TextField,
    Typography,
} from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { NotificationActions, RootStateType } from "@Store";
import { actions as dialogActions } from "@Store/Reducers/dialog";
import { DialogComponents } from "@Interfaces/Entities/Dialogs";
import { postPollTextFieldSubmission } from "@Services/API/PollTextField";
import { ITextFieldCollection } from "@Interfaces";
import UserAvatar from "@Components/PollItem/UserAvatar";
import {
    StyledAvatarWrapper,
    StyledHeaderInnerWrapper,
    StyledHeaderInfo,
    StyledHeaderMenu,
    StyledHeaderWrapper,
    StyledQuestion,
    StyledTime,
    StyledUsername,
} from "@Components/PollItem/PollItemHeader/PollItemHeader.styled";
import { StyledPollItemWrapper } from "@Components/PollItem/styled";
import MoreHorizIcon from "@mui/icons-material/MoreHoriz";
// import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import moment from "moment";
import { ResourceImage } from "@Interfaces/Entities/Poll";
import { CommentsIcon } from "@Components/Icons";
import { useTextFieldResponses } from "@Hooks/useTextFieldResponses";
import TextFieldResponsesList from "./TextFieldResponsesList";

interface Props {
    textField: ITextFieldCollection;
    author?: {
        username: string;
        avatar?: ResourceImage;
    };
    published?: string;
    onEditTextField?: (
        textFieldId: string,
        question: string
    ) => Promise<void> | void;
    onDeleteTextField?: (textFieldId: string) => Promise<void> | void;
    onSubmitted?: (textFieldId: string) => void;
    // True when the viewer owns the channel. Only the owner-side page knows this;
    // other pages leave it unset and fall back to the creator check.
    isChannelAdmin?: boolean;
}

const TextFieldCollectionCard: React.FC<Props> = ({
    textField,
    author,
    published,
    onEditTextField,
    onDeleteTextField,
    onSubmitted,
    isChannelAdmin = false,
}) => {
    const dispatch = useDispatch<any>();
    const { t, i18n } = useTranslation();
    const currentUserId = useSelector(
        (state: RootStateType) => state.auth.user?.id
    );
    const isAuthenticated = useSelector(
        (state: RootStateType) => state.auth.isAuthenticated
    );
    // Creator check: user must be authenticated AND have matching ID
    const isTextFieldCreator =
        isAuthenticated && currentUserId && textField.created_by
            ? textField.created_by === currentUserId
            : false;
    // The owner manages every field in their channel, including ones created by
    // former members. For new fields the creator is always the owner.
    const canManageTextField =
        isAuthenticated && (isChannelAdmin || isTextFieldCreator);
    const [responseDraft, setResponseDraft] = useState("");
    const [submitting, setSubmitting] = useState(false);
    const [hasSubmitted, setHasSubmitted] = useState(false);
    const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
    const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
    const [editQuestion, setEditQuestion] = useState(textField.question);
    const [isSavingEdit, setIsSavingEdit] = useState(false);
    const [displayedQuestion, setDisplayedQuestion] = useState(
        textField.question
    );

    // Hook for managing text field responses
    const { isExpanded, responses, isLoading, error, toggleExpanded } =
        useTextFieldResponses(textField._id);

    /*
    const responseLabel = useMemo(
        () =>
            t("home.tabs.textfield_responses", {
                count: responsesCount,
            }),
        [t, responsesCount]
    );
    
    const lastResponse = useMemo(() => {
        if (!lastResponseAt) return null;
        return new Date(lastResponseAt).toLocaleString();
    }, [lastResponseAt]);
    */
    const placeholder = t("home.tabs.textfield_placeholder");

    // Build author object from textField.data or fallback to author prop
    const authorInfo = {
        username:
            textField.data?.username ??
            author?.username ??
            t("home.tabs.textfield_default_author"),
        avatar: textField.data?.avatar ?? author?.avatar,
    };

    const displayUsername: string = authorInfo.username as string;
    const publishedTime = published ?? textField.createdAt;
    const displayAvatar = authorInfo.avatar;

    const handleSubmitResponse = useCallback(
        async (event?: React.FormEvent<HTMLFormElement>) => {
            if (event) {
                event.preventDefault();
            }
            const trimmedResponse = responseDraft.trim();
            if (!trimmedResponse) {
                return;
            }
            setSubmitting(true);
            try {
                await postPollTextFieldSubmission(textField._id, {
                    response: trimmedResponse,
                });
                dispatch(
                    NotificationActions.notification({
                        snackbarOpen: true,
                        snackbarType: "success",
                        snackbarMessage: t(
                            "home.tabs.textfield_submit_success"
                        ),
                    })
                );
                setResponseDraft("");
                setHasSubmitted(true);
                onSubmitted?.(textField._id);
            } catch (error) {
                dispatch(
                    NotificationActions.notification({
                        snackbarOpen: true,
                        snackbarType: "error",
                        snackbarMessage: t("home.tabs.textfield_submit_error"),
                    })
                );
            } finally {
                setSubmitting(false);
            }
        },
        [dispatch, onSubmitted, responseDraft, t, textField._id]
    );

    const timeLabel = publishedTime
        ? moment(publishedTime).locale(i18n.language).fromNow()
        : t("home.tabs.textfield_created_at_unknown");

    // Disable if: owner is viewing OR already submitted OR currently submitting
    // Allow authenticated users, guest flow users, and anonymous voters
    const isInteractionDisabled =
        canManageTextField || hasSubmitted || submitting;
    let submissionHelper: string | null = null;
    if (canManageTextField) {
        submissionHelper = t("home.tabs.textfield_admin_readonly");
    } else if (hasSubmitted) {
        submissionHelper = t("home.tabs.textfield_vote_submitted");
    }

    useEffect(() => {
        setEditQuestion(textField.question);
        setDisplayedQuestion(textField.question);
    }, [textField.question]);

    const isMenuOpen = Boolean(menuAnchor);
    const canManageTextFieldWithActions =
        canManageTextField && (onEditTextField || onDeleteTextField);
    const isEditSaveDisabled = editQuestion.trim().length === 0 || isSavingEdit;

    const handleMenuOpen = (event: React.MouseEvent<HTMLButtonElement>) => {
        setMenuAnchor(event.currentTarget);
    };

    const handleMenuClose = () => {
        setMenuAnchor(null);
    };

    const handleEditOpen = () => {
        handleMenuClose();
        setIsEditDialogOpen(true);
    };

    const handleEditClose = () => {
        setIsEditDialogOpen(false);
        setEditQuestion(textField.question);
    };

    const handleSaveEdit = async () => {
        if (!onEditTextField) {
            handleEditClose();
            return;
        }

        setIsSavingEdit(true);
        try {
            const newQuestion = editQuestion.trim();
            // Update displayed question immediately for instant UI feedback
            setDisplayedQuestion(newQuestion);
            await Promise.resolve(onEditTextField(textField._id, newQuestion));
            handleEditClose();
        } catch (error) {
            console.error(error);
            // Revert displayed question on error
            setDisplayedQuestion(textField.question);
        } finally {
            setIsSavingEdit(false);
        }
    };

    const handleDeleteClick = () => {
        handleMenuClose();
        dispatch(
            dialogActions.openDialog({
                id: DialogComponents.DeleteTextFieldDialog,
                props: {
                    textFieldId: textField._id,
                    channelId: textField.channel_id,
                    onConfirmDelete: onDeleteTextField
                        ? () =>
                              Promise.resolve(onDeleteTextField(textField._id))
                        : undefined,
                },
            })
        );
    };

    return (
        <>
            <StyledPollItemWrapper
                key={`text-field-${textField._id}`}
                className="tb-shadow-box"
            >
                <StyledHeaderWrapper>
                    <StyledHeaderInnerWrapper>
                        <StyledAvatarWrapper>
                            <UserAvatar
                                username={displayUsername}
                                avatar={displayAvatar}
                                size="medium"
                            />
                        </StyledAvatarWrapper>
                        <StyledHeaderInfo>
                            <StyledUsername>
                                {displayUsername}
                                <StyledTime>{timeLabel}</StyledTime>
                            </StyledUsername>
                            <StyledQuestion>{displayedQuestion}</StyledQuestion>
                        </StyledHeaderInfo>
                        <StyledHeaderMenu>
                            {canManageTextFieldWithActions ? (
                                <>
                                    <IconButton
                                        aria-label="options"
                                        size="small"
                                        edge="end"
                                        onClick={handleMenuOpen}
                                    >
                                        <MoreHorizIcon fontSize="small" />
                                    </IconButton>
                                    <Menu
                                        anchorEl={menuAnchor}
                                        keepMounted
                                        open={isMenuOpen}
                                        onClose={handleMenuClose}
                                    >
                                        {onEditTextField && (
                                            <MenuItem onClick={handleEditOpen}>
                                                {t(
                                                    "home.tabs.textfield_menu_edit"
                                                )}
                                            </MenuItem>
                                        )}
                                        {onDeleteTextField && (
                                            <MenuItem
                                                onClick={handleDeleteClick}
                                            >
                                                {t(
                                                    "home.tabs.textfield_menu_delete"
                                                )}
                                            </MenuItem>
                                        )}
                                    </Menu>
                                </>
                            ) : (
                                <IconButton
                                    aria-label="options"
                                    size="small"
                                    edge="end"
                                    disabled
                                >
                                    <MoreHorizIcon fontSize="small" />
                                </IconButton>
                            )}
                        </StyledHeaderMenu>
                    </StyledHeaderInnerWrapper>
                </StyledHeaderWrapper>
                <div style={{ padding: "16px" }}>
                    {/* TODO: Hide previous responses per user feedback - don't show last response text
                    {textField.last_response_text && (
                        <Typography
                            variant="body2"
                            color="textSecondary"
                            className={classes.stat}
                        >
                            {t("home.tabs.textfield_last_response_text", {
                                text: textField.last_response_text,
                            })}
                        </Typography>
                    )}
                    */}
                    <Divider style={{ marginTop: 16, marginBottom: 8 }} />
                    <form
                        style={{
                            marginTop: "16px",
                            display: "flex",
                            flexDirection: "column",
                            gap: "8px",
                        }}
                        onSubmit={handleSubmitResponse}
                        noValidate
                    >
                        <TextField
                            variant="outlined"
                            placeholder={placeholder}
                            value={responseDraft}
                            onChange={(event) =>
                                setResponseDraft(event.target.value)
                            }
                            multiline
                            minRows={3}
                            required={false}
                            disabled={isInteractionDisabled}
                            inputProps={{
                                "aria-label": textField.question,
                            }}
                        />
                        <div
                            style={{
                                display: "flex",
                                justifyContent: "flex-end",
                            }}
                        >
                            <Button
                                type="submit"
                                variant="contained"
                                color="primary"
                                disabled={
                                    isInteractionDisabled ||
                                    responseDraft.trim() === ""
                                }
                            >
                                {t("home.tabs.textfield_submit")}
                            </Button>
                        </div>
                    </form>
                    {submissionHelper && (
                        <Typography
                            variant="caption"
                            color="textSecondary"
                            sx={{ mt: 0.5 }}
                        >
                            {submissionHelper}
                        </Typography>
                    )}
                </div>
                <Divider />
                {/* Text Field Responses Footer - Only visible to the owner */}
                {canManageTextField && (
                    <>
                        <div
                            style={{
                                padding: "12px",
                                borderTop: "1px solid rgba(0, 0, 0, 0.12)",
                            }}
                        >
                            <div
                                style={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: "8px",
                                }}
                            >
                                <IconButton
                                    size="small"
                                    onClick={toggleExpanded}
                                    aria-label="Toggle responses"
                                    aria-expanded={isExpanded}
                                >
                                    <CommentsIcon />
                                </IconButton>
                                <Typography variant="body2">
                                    {t("home.tabs.textfield_responses", {
                                        count: textField.responses_count,
                                        defaultValue: "Responses: {{count}}",
                                    })}
                                </Typography>
                            </div>
                        </div>
                        <TextFieldResponsesList
                            responses={responses}
                            isExpanded={isExpanded}
                            isLoading={isLoading}
                            error={error}
                        />
                    </>
                )}
                {/* TODO: Hide response count and last response time per user feedback
                <div className={classes.bottomBar}>
                    <div className={classes.gradient} />
                    <div className={classes.bottomRow}>
                        <CheckCircleOutlineIcon className={classes.checkIcon} />
                        <Typography variant="body2" color="textSecondary">
                            {responseLabel}
                        </Typography>
                        {lastResponse && (
                            <Typography
                                variant="caption"
                                color="textSecondary"
                                className={classes.bottomMeta}
                            >
                                {t("home.tabs.textfield_last_response", {
                                    time: lastResponse,
                                })}
                            </Typography>
                        )}
                    </div>
                </div>
                */}
            </StyledPollItemWrapper>
            <Dialog
                open={isEditDialogOpen}
                onClose={handleEditClose}
                fullWidth
                maxWidth="sm"
            >
                <DialogTitle>{t("home.tabs.textfield_menu_edit")}</DialogTitle>
                <DialogContent>
                    <TextField
                        fullWidth
                        variant="outlined"
                        label={t("home.tabs.textfield_edit_label")}
                        value={editQuestion}
                        onChange={(event) =>
                            setEditQuestion(event.target.value)
                        }
                        multiline
                        rows={3}
                        inputProps={{ maxLength: 400 }}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleEditClose} color="inherit">
                        {t("home.tabs.textfield_edit_cancel")}
                    </Button>
                    <Button
                        onClick={handleSaveEdit}
                        color="primary"
                        variant="contained"
                        disabled={isEditSaveDisabled}
                    >
                        {t("home.tabs.textfield_edit_save")}
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
};

export default TextFieldCollectionCard;
